/* ==========================================================================
   Image floating window
   Corner-anchored promo image with its OWN daily dismissal state:

     1. Click  → dispatches `newsletter-popup:open`, which the subscription
                 popup listens for and opens itself (deliberate, force).
     2. State  → subscribed is shared (hard gate); the daily dismissal is
                 independent (marstek_float_dismissed vs
                 marstek_popup_dismissed), so closing one no longer hides
                 the other. Both states live in window.MarstekNewsletter.
   ========================================================================== */

class ImageFloatingWindow extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.visible = false;
    this.dismissed = false;

    if (this.isBlockedPage()) return;

    this.bindEvents();

    // In the theme editor canvas, always show so the merchant can position it.
    if (this.isDesignMode()) {
      this.show("preview", true);
      return;
    }

    if (this.isOutsideHomepage()) return;

    if (this.isSubscribed() || this.hasDismissedToday("float")) return;

    // Storefront / visual preview links: behave like a real visitor.
    this.startTriggers();
  }

  bindEvents() {
    this.querySelector("[data-image-floating-open]")?.addEventListener("click", () => this.handleOpen());
    this.querySelector("[data-image-floating-close]")?.addEventListener("click", () => this.dismiss());

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && this.visible) this.dismiss();
    });

    // Hide while the subscription/promo popup is on screen, restore afterwards
    // unless the visitor dismissed the FLOATING WINDOW or subscribed in the
    // meantime. Closing the popup no longer writes the float's dismissal, so
    // the artwork comes back by itself.
    this.popupShowHandler = () => this.hide();
    this.popupCloseHandler = () => {
      if (this.dismissed || this.isSubscribed() || this.hasDismissedToday("float")) return;
      this.show("popup-close");
    };
    window.addEventListener("popup_show", this.popupShowHandler);
    window.addEventListener("popup_close", this.popupCloseHandler);
  }

  handleOpen() {
    if (this.dataset.openPopup === "false") return;
    if (this.isSubscribed()) return;

    window.dispatchEvent(
      new CustomEvent("newsletter-popup:open", {
        detail: { trigger: "floating_window", force: true, source: "floating_window" },
      }),
    );
    this.track("floating_window_click");
  }

  show(trigger, force = false) {
    // Hard gate: a subscribed visitor never sees the artwork again, not even
    // from a forced preview open.
    if (this.isSubscribed()) return;
    if (
      !force &&
      (this.visible || this.dismissed || this.hasDismissedToday("float"))
    ) {
      return;
    }

    this.visible = true;
    this.clearTriggers();
    this.classList.add("is-visible");
    this.setAttribute("aria-hidden", "false");
    this.track("floating_window_show", { trigger });
  }

  hide() {
    if (!this.visible) return;

    this.visible = false;
    this.classList.remove("is-visible");
    this.setAttribute("aria-hidden", "true");
  }

  dismiss() {
    this.dismissed = true;
    this.markDismissedToday("float");
    this.hide();
    this.track("floating_window_close");
  }

  startTriggers() {
    const delay = Math.max(Number(this.dataset.triggerDelay) || 5, 1) * 1000;
    const scrollThreshold = Math.min(Math.max(Number(this.dataset.triggerScroll) || 30, 1), 99);

    this.delayTimer = window.setTimeout(() => this.show("time"), delay);

    this.scrollHandler = () => {
      const totalScrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (totalScrollableHeight > 0 && (window.scrollY / totalScrollableHeight) * 100 >= scrollThreshold) {
        this.show("scroll");
      }
    };
    window.addEventListener("scroll", this.scrollHandler, { passive: true });

    if (this.dataset.exitIntent === "true" && !window.matchMedia("(max-width: 899px)").matches) {
      // Arm exit intent only after the time-trigger delay has elapsed, so it
      // can't preempt the configured time/scroll triggers.
      this.exitIntentTimer = window.setTimeout(() => {
        this.exitIntentHandler = (event) => {
          if (event.clientY <= 0) this.show("exit");
        };
        document.addEventListener("mouseout", this.exitIntentHandler);
      }, delay);
    }
  }

  clearTriggers() {
    window.clearTimeout(this.delayTimer);
    window.clearTimeout(this.exitIntentTimer);
    if (this.scrollHandler) window.removeEventListener("scroll", this.scrollHandler);
    if (this.exitIntentHandler) document.removeEventListener("mouseout", this.exitIntentHandler);
  }

  disconnectedCallback() {
    this.clearTriggers();
    if (this.popupShowHandler) window.removeEventListener("popup_show", this.popupShowHandler);
    if (this.popupCloseHandler) window.removeEventListener("popup_close", this.popupCloseHandler);
  }

  isBlockedPage() {
    return /\/checkout(?:\/|$)|\/orders?(?:\/|$)|\/thank[_-]?you(?:\/|$)/i.test(window.location.pathname);
  }

  isOutsideHomepage() {
    return this.dataset.homepageOnly === "true" && this.dataset.pageIsHome !== "true";
  }

  isDesignMode() {
    return window.Shopify?.designMode === true;
  }

  isPreviewMode() {
    return (
      this.dataset.testMode === "true" ||
      window.MarstekNewsletter?.isPreviewMode?.() === true
    );
  }

  // Highest-priority gate. Preview ignores it so merchants can always test.
  isSubscribed() {
    if (this.isPreviewMode()) return false;
    return (
      this.dataset.hasSubscription === "true" ||
      window.MarstekNewsletter?.isSubscribed?.() === true
    );
  }

  hasDismissedToday(kind) {
    return window.MarstekNewsletter?.hasDismissedToday?.(kind) === true;
  }

  markDismissedToday(kind) {
    window.MarstekNewsletter?.markDismissedToday?.(kind);
  }

  track(eventName, detail = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...detail });

    if (window.Shopify?.analytics?.publish) {
      window.Shopify.analytics.publish(eventName, detail);
    }

    window.dispatchEvent(new CustomEvent(eventName, { detail }));
  }
}

if (!customElements.get("image-floating-window")) {
  customElements.define("image-floating-window", ImageFloatingWindow);
}
